import React, { Fragment, memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import useGetFeaturedListings from '../../../hooks/useGetFeaturedListings';
import { TRANSLATIONS } from '../../../../translations';
import moment from 'moment';

import SmartSearchActivityCard from './SmartSearchActivityCard';
import SmartSearchCategoryCard from './SmartSearchCategoryCard';
import Billboard from '../../../ui-components/Billboard/Billboard';
import ControlledCarousel from '../../../ui-components/Carousel/ControlledCarousel';
import Skeleton from '../../../ui-components/Skeleton/Skeleton';

function SmartSearchBillboards({ popularCategories, carouselWrapped }) {
  const userLocation = JSON.parse(localStorage.getItem('userLocation'));
  const locationData = JSON.parse(localStorage.getItem('location_data'));
  const location = userLocation ? userLocation : locationData;

  const commonParams = {
    _limit: 20,
    'available_date[gte]': moment().format('YYYY-MM-DD'),
    'available_date[lte]': moment().add(30, 'days').format('YYYY-MM-DD'),
  };

  if (location) {
    commonParams.latitude = location.latitude;
    commonParams.longitude = location.longitude;
    commonParams.distance = 50;
  }

  const date = moment().subtract(60, 'days').format('YYYY-MM-DD');
  const newListingsParams = {
    ...commonParams,
    'published_at[gte]': date,
  };

  const newListings = useGetFeaturedListings(newListingsParams, commonParams);
  const topListings = useGetFeaturedListings(commonParams);

  const isLoading =
    popularCategories.isLoading &&
    topListings.isLoading &&
    newListings.isLoading;

  const renderActivityCards = useCallback((listings) => {
    return listings?.map((item, index) => (
      <Fragment key={index}>
        <SmartSearchActivityCard item={item} index={index} />
      </Fragment>
    ));
  }, []);

  /** @param {number | null} rowsNumber - Number of category rows (2 x cards) to render. */
  const renderCategoryCards = useCallback(
    (rowsNumber = null) => {
      return popularCategories?.categories?.map((item, index) => {
        const condition = rowsNumber
          ? index <= rowsNumber && index % 2 === 0
          : index % 2 === 0;
        rowsNumber % 3 === 0 ? rowsNumber++ : rowsNumber;
        return condition ? (
          <div
            key={index}
            className="smart-search__container--body--featured--pair"
          >
            <SmartSearchCategoryCard
              classNames="categories--item"
              item={item}
              index={index}
            />
            <SmartSearchCategoryCard
              classNames="categories--item"
              item={popularCategories?.categories[index + 1]}
              index={index + 1}
            />
          </div>
        ) : null;
      });
    },
    [popularCategories]
  );

  const billboardsData = useMemo(
    () => ({
      // eslint-disable-next-line no-undef
      [TRANSLATIONS.REQUESTED_CATEGORIES]: renderCategoryCards(3),
      // eslint-disable-next-line no-undef
      [TRANSLATIONS.REQUESTED_EXPERIENCES]: renderActivityCards(
        topListings.listings.slice(0, 5)
      ),
      // eslint-disable-next-line no-undef
      [TRANSLATIONS.NOVELTY]: renderActivityCards(newListings.listings).slice(
        0,
        5
      ),
    }),
    [
      renderCategoryCards,
      renderActivityCards,
      topListings.listings,
      newListings.listings,
      popularCategories,
    ]
  );

  const renderBillboards = useCallback(
    Object.entries(billboardsData)?.map(([key, value]) => (
      <Billboard key={key} title={key}>
        {value}
      </Billboard>
    )),
    [billboardsData]
  );

  const billboardSkeleton = (
    <>
      <div className="d-flex flex-column gap-s" style={{ minWidth: '269px' }}>
        <Skeleton classNames="radius-rounded" width={'100%'} height={24} />
        <Skeleton classNames="radius-s" width={'100%'} height={370} />
      </div >
      <div className="d-flex flex-column gap-s" style={{ minWidth: '300px' }}>
        <Skeleton classNames="radius-rounded" width={'100%'} height={24} />
        <Skeleton classNames="radius-s" width={'100%'} height={370} />
      </div >
      <div className="d-flex flex-column gap-s" style={{ minWidth: '300px' }}>
        <Skeleton classNames="radius-rounded" width={'100%'} height={24} />
        <Skeleton classNames="radius-s" width={'100%'} height={370} />
      </div >
    </>
  );

  if (isLoading) {
    if (carouselWrapped) {
      return (
        <div className="pl-xs d-flex gap-2xs" style={{ overflow: "auto", scrollbarWidth: "none" }}>
          {billboardSkeleton}
        </div>
      );
    }

    return billboardSkeleton;
  }

  //TODO: that's a quick fix
  if (carouselWrapped) {
    return (
      <ControlledCarousel classNames="gap-2xl">
        {renderBillboards}
      </ControlledCarousel>
    );
  }

  return renderBillboards;
}

SmartSearchBillboards.propTypes = {
  carouselWrapped: PropTypes.bool,
  popularCategories: PropTypes.object,
};

SmartSearchBillboards.defaultProps = {
  carouselWrapped: false,
  popularCategories: { categories: [], isLoading: false, error: null },
};

export default memo(SmartSearchBillboards);
